<script lang="ts" setup>
import BaseDialog from '@/components/dialogs/BaseDialog.vue'
import type { PaymentMethodType } from '@chargebee/chargebee-js-types/cb-types/internal/payment-intent/types'

const { t } = useI18n()

const props = defineProps<{
  intent: SubscriptionPaymentIntent
  address: Address
  selection: CheckoutSelection
  seats: number
  coupon: string
  loading: boolean // used by DialogControl
}>()

const emit = defineEmits<{
  action: [intentId: string]
  close: []
}>()

const widgetStore = useWidgetStore()
const { formatCurrency } = useCurrency()

const selectionRef = computed(() => props.selection)
const seatsRef = computed(() => props.seats)
const couponRef = computed(() => props.coupon)

const { paymentData, generatePaymentIntent, error } = usePaymentIntent(selectionRef, seatsRef, couponRef)

watch(error, err => {
  if (err) {
    const message = t('billing.settings.cc.error.setup')
    widgetStore.createSnackbar({ message, color: 'error' })
  }
})

const isSubmitting = ref(false)

const paymentMethodTypes = computed(() =>
  // FIXME: SEPA direct debit should only be shown for EU.
  props.address.country !== 'CH' ? ['card', 'direct_debit'] : (['card'] as PaymentMethodType[])
)

const onPaymentValid = () => {
  paymentData.isValid = true
}

const onPaymentError = () => {
  isSubmitting.value = false
}

const onPaymentMethodUpdate = async (e: PaymentComponentUpdateEvent) => {
  paymentData.paymentMethodType = e.paymentMethodType
  await generatePaymentIntent(props.address)
}

const onPaymentSuccess = async (e: PaymentComponentSuccessEvent) => {
  emit('action', { intentId: e.paymentIntentId })
  emit('close')
}

const displayPrice = computed(() => {
  if (!props.intent) return 0
  return props.intent.amountSubtotal - props.intent.amountDiscountTotal
})

const paymentComponent = useTemplateRef('paymentComponent')

const submit = async () => {
  if (isSubmitting.value) return
  isSubmitting.value = true

  try {
    const isValid = await paymentComponent.value?.validate()
    if (isValid) {
      paymentComponent.value?.submit()
    } else {
      isSubmitting.value = false
    }
  } catch {
    isSubmitting.value = false
  }
}
</script>

<template>
  <base-dialog
    :title="t('global.payment_method')"
    :cancel-text="t('global.back')"
    :action-text="t('business.upgrade.plan.pay')"
    :loading="isSubmitting"
    data-cy="dialog_leave_page"
    persistent
    max-width="480"
    @action="submit"
    @close="emit('close')"
  >
    <payment-element
      v-if="intent"
      ref="paymentComponent"
      class="mb-6"
      :currency="intent.currency"
      :intent="intent"
      :payment-method-types="paymentMethodTypes"
      @valid="onPaymentValid"
      @update="onPaymentMethodUpdate"
      @success="onPaymentSuccess"
      @error="onPaymentError"
    />
    <v-divider class="mx-n8" />
    <div class="px-8 pt-7 pb-2 text-center text-grey-darker">
      <i18n-t keypath="business.upgrade.plan.due_today">
        <template #cycle>
          <span>
            {{
              selection.billingCycle === 'yearly'
                ? $t('billing.billing_cycle.per_year')
                : $t('billing.billing_cycle.per_month')
            }}
          </span>
        </template>
        <template #price>
          <span class="font-weight-bold" data-cy="total_price_text">{{
            formatCurrency(displayPrice ?? 0, intent.currency)
          }}</span>
        </template>
      </i18n-t>
    </div>
  </base-dialog>
</template>

<style lang="scss"></style>
